import React, { useState, useRef } from 'react';
import { View, Image, Text, StyleSheet, TouchableOpacity, Modal, Animated, Pressable, Dimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme, getColor } from '../../../theme/useTheme';
import { getMediaUrl } from '../../../utils/media';
import ZoomableImage from './ZoomableImage';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

/**
 * UserAvatar
 *
 * Props:
 *  source       – image URL string or require() number
 *  name         – display name (used for initials fallback)
 *  size         – diameter in px (default 40)
 *  showOnline   – show the online indicator dot
 *  isOnline     – online status value
 *  icon         – Ionicons name override for the fallback circle
 *  onPress      – if provided the avatar is tappable and opens an enlarged modal
 */
const UserAvatar = ({
    source,
    name,
    size = 40,
    showOnline = false,
    isOnline = false,
    icon = null,
    onPress,
}) => {
    const { theme } = useTheme();
    const [lightboxVisible, setLightboxVisible] = useState(false);
    const scaleAnim = useRef(new Animated.Value(0.7)).current;
    const opacityAnim = useRef(new Animated.Value(0)).current;

    const getInitials = (name) => {
        if (!name) return 'U';
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const imageSource = typeof source === 'string' ? { uri: getMediaUrl(source) } : source;

    // ── Lightbox helpers ─────────────────────────────────────────────────────
    const openLightbox = () => {
        setLightboxVisible(true);
        Animated.parallel([
            Animated.spring(scaleAnim,   { toValue: 1,    useNativeDriver: true, bounciness: 10 }),
            Animated.timing(opacityAnim, { toValue: 1, duration: 200, useNativeDriver: true }),
        ]).start();
    };

    const closeLightbox = () => {
        Animated.parallel([
            Animated.spring(scaleAnim,   { toValue: 0.7, useNativeDriver: true }),
            Animated.timing(opacityAnim, { toValue: 0,   duration: 180, useNativeDriver: true }),
        ]).start(() => setLightboxVisible(false));
    };

    const handlePress = () => {
        if (onPress) {
            onPress();
            return;
        }
        // Default: open enlarged avatar if the user has a profile image
        if (source) openLightbox();
    };

    // ── Avatar core ──────────────────────────────────────────────────────────
    const avatarCore = (
        <View style={[styles.container, { width: size, height: size }]}>
            {source ? (
                <Image
                    source={imageSource}
                    style={[styles.image, { borderRadius: size / 2 }]}
                />
            ) : (
                <View style={[styles.fallback, { borderRadius: size / 2, backgroundColor: getColor('primary.main') }]}>
                    {icon ? (
                        <Ionicons name={icon} size={size / 1.8} color="#FFFFFF" />
                    ) : (
                        <Text style={[styles.initials, { fontSize: size / 2.5 }]}>{getInitials(name)}</Text>
                    )}
                </View>
            )}

            {showOnline && (
                <View style={[
                    styles.onlineBadge,
                    {
                        backgroundColor: isOnline ? '#10B981' : '#9CA3AF',
                        width: size / 4,
                        height: size / 4,
                        borderRadius: size / 8,
                        borderWidth: 2,
                        borderColor: '#FFFFFF',
                    }
                ]} />
            )}
        </View>
    );

    // Tappable wrapper (only when avatar has an image or onPress provided)
    const tappable = source || onPress;

    return (
        <>
            {tappable ? (
                <TouchableOpacity
                    activeOpacity={0.8}
                    onPress={handlePress}
                    accessibilityRole="button"
                    accessibilityLabel={`View ${name || 'user'} profile photo`}
                >
                    {avatarCore}
                </TouchableOpacity>
            ) : (
                avatarCore
            )}

            {/* ── Enlarged avatar lightbox ─────────────────────────────── */}
            {source && (
                <Modal
                    visible={lightboxVisible}
                    transparent
                    animationType="none"
                    onRequestClose={closeLightbox}
                    statusBarTranslucent
                >
                    <Animated.View style={[styles.lightboxBackdrop, { opacity: opacityAnim }]}>
                        <Pressable style={StyleSheet.absoluteFill} onPress={closeLightbox} />

                        <Animated.View style={[styles.avatarLarge, { transform: [{ scale: scaleAnim }] }]}>
                            <ZoomableImage
                                uri={typeof source === 'string' ? getMediaUrl(source) : source}
                                width={SCREEN_WIDTH * 0.9}
                                height={SCREEN_HEIGHT * 0.6}
                                resizeMode="contain"
                                borderRadius={12}
                                onPress={closeLightbox}
                            />
                        </Animated.View>

                        {/* Name label */}
                        {name && (
                            <Animated.Text
                                style={[styles.lightboxName, { opacity: opacityAnim }]}
                                numberOfLines={1}
                            >
                                {name}
                            </Animated.Text>
                        )}

                        {/* Close button */}
                        <TouchableOpacity style={styles.lightboxClose} onPress={closeLightbox} activeOpacity={0.8}>
                            <Ionicons name="close" size={24} color="#FFFFFF" />
                        </TouchableOpacity>
                    </Animated.View>
                </Modal>
            )}
        </>
    );
};

const AVATAR_LARGE_SIZE = Math.min(SCREEN_WIDTH * 0.75, 300);

const styles = StyleSheet.create({
    container: {
        position: 'relative',
    },
    image: {
        width: '100%',
        height: '100%',
    },
    fallback: {
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    initials: {
        color: '#FFFFFF',
        fontWeight: 'bold',
    },
    onlineBadge: {
        position: 'absolute',
        bottom: 0,
        right: 0,
    },

    // Lightbox
    lightboxBackdrop: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.88)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    avatarLarge: {
        width: SCREEN_WIDTH * 0.9,
        height: SCREEN_HEIGHT * 0.6,
        overflow: 'hidden',
        borderRadius: 12,
        backgroundColor: 'rgba(0,0,0,0.5)',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.5,
        shadowRadius: 24,
        elevation: 16,
    },
    avatarLargeImage: {
        width: '100%',
        height: '100%',
    },
    lightboxName: {
        marginTop: 20,
        color: '#FFFFFF',
        fontSize: 18,
        fontWeight: '700',
        letterSpacing: 0.3,
        maxWidth: SCREEN_WIDTH * 0.8,
        textAlign: 'center',
    },
    lightboxClose: {
        position: 'absolute',
        top: 52,
        right: 18,
        backgroundColor: 'rgba(255,255,255,0.15)',
        borderRadius: 22,
        padding: 9,
    },
});

export default UserAvatar;
